import { ColDef, ColumnApi, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { ColumnState } from '@ag-grid-community/core/dist/cjs/es5/columns/columnModel';
import { AGTable as Table, agTableGetColumns, Loader, useGlobalModalContext } from '@creditsights/styleguide-web';
import React, {
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EXPORT_EXCEL_SHEET_NAME } from '../../../../../Base.constants';
import { usePreviousValue } from '../../../../../hooks/common';
import useTableQueryParams from '../../../../../hooks/table/useTableQueryParams';
import { RpColumn } from '../../../../../models/RpColumn';
import { MonitorDto } from '../../../../../services/dtos/riskProducts/monitor.dto';
import { EsField } from '../../../../../services/dtos/riskProducts/rpColumn.dto';
import { MODAL_TYPES } from '../../../../GlobalModal';
import NoResults from '../../../../NoResults';
import { ManageColumnsModalProps } from '../../ManageColumnsModal/ManageColumnsModal';
import { COLUMN_PERSISTENCE_KEY, DEFAULT_MONITOR_SORT_FIELD } from '../Monitor.constants';
import {
  createExportFileName,
  getColDefForColumns,
  mapRpColumnToDefinition,
  getCellValue,
  getMonitorManageColumns,
} from '../Monitor.logic';
import { RP_FIELDS_ADMIN_SPECIFIC_EXPORT } from '../../../common/widget.constants';
import { columnSortDefsGet, getInitialMonitorColumns } from './MonitorTable.logic';

interface MonitorTableProps {
  watchListId: string;
  rpColumns: RpColumn[];
  data?: MonitorDto[];
  searchText?: string;
  isExpandedTableShown?: boolean;
  exportAllFields?: boolean;
}

export interface MonitorTableHandle {
  onExport: () => void;
  onManageColumns: () => void;
}

const CompanyTable = Table<MonitorDto>;

const MonitorTable = (
  { watchListId, rpColumns, data, searchText, isExpandedTableShown, exportAllFields }: MonitorTableProps,
  ref
): ReactElement => {
  const { showModal, setProps } = useGlobalModalContext();
  const columnApiRef = useRef<ColumnApi | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const allColumns = useMemo(() => rpColumns.map(mapRpColumnToDefinition), [rpColumns]);

  const [columnDefs, setColumnDefs] = useState<ColDef<MonitorDto>[]>(
    agTableGetColumns<MonitorDto>(COLUMN_PERSISTENCE_KEY, getInitialMonitorColumns(allColumns), allColumns, false)
  );
  const wasExpandedTableShownPreviously = usePreviousValue(isExpandedTableShown);
  const [isModalOpen, setIsModalOpen] = useState<boolean>();

  const { sort, onSortChanged } = useTableQueryParams([watchListId], DEFAULT_MONITOR_SORT_FIELD);

  useEffect(() => {
    // This useEffect is used to sync column state for not expanded monitor table after making the changes in expanded table and closing it
    if (wasExpandedTableShownPreviously && !isExpandedTableShown) {
      const newDefinitions = agTableGetColumns<MonitorDto>(
        COLUMN_PERSISTENCE_KEY,
        getInitialMonitorColumns(allColumns),
        allColumns,
        false
      );

      const column = newDefinitions.find((c) => c.colId === sort.sortField);
      if (column) {
        column.sort = sort.sortOrderAsc ? 'asc' : 'desc';
      }
      columnApiRef.current?.applyColumnState({
        state: newDefinitions as ColumnState[],
        applyOrder: true,
        defaultState: { pinned: null, sort: null, sortIndex: null },
      });
      setColumnDefs(newDefinitions);
    }
  }, [isExpandedTableShown, wasExpandedTableShownPreviously, sort]);

  useImperativeHandle(
    ref,
    () => ({
      onExport: exportDataToExcel,
      onManageColumns,
    }),
    []
  );

  const manageColumnsProps: ManageColumnsModalProps = useMemo(
    () => ({
      rpColumns: getMonitorManageColumns(rpColumns),
      checkedColumns: columnDefs.map((c) => c.colId as string),
      setCheckedColumns: (esFields: EsField[]) => {
        const newDefs = getColDefForColumns(esFields, rpColumns);
        setColumnDefs(newDefs);
      },
      resetColumns: () => {
        setColumnDefs(getInitialMonitorColumns(allColumns));
      },
      onClose: () => {
        setIsModalOpen(false);
      },
    }),
    [rpColumns, columnDefs]
  );

  const onManageColumns = () => {
    setIsModalOpen(true);
    showModal(MODAL_TYPES.MANAGE_COLUMNS, manageColumnsProps);
  };

  const exportDataToExcel = () => {
    const columnDefs = gridApiRef.current?.getColumnDefs();
    let columnKeys: string[] = columnDefs ? columnDefs.map((columnDef: ColDef<any>) => columnDef.field as string) : [];
    if (!exportAllFields) {
      columnKeys = columnKeys.filter((columnKey) => !RP_FIELDS_ADMIN_SPECIFIC_EXPORT.includes(columnKey));
    }

    gridApiRef.current?.exportDataAsExcel({
      processCellCallback: getCellValue,
      fileName: createExportFileName(),
      sheetName: EXPORT_EXCEL_SHEET_NAME,
      columnKeys,
    });
  };

  useEffect(() => {
    if (isModalOpen) {
      setProps(MODAL_TYPES.MANAGE_COLUMNS, manageColumnsProps);
    }
  }, [manageColumnsProps, isModalOpen]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    columnApiRef.current = event.columnApi;
    event.columnApi.applyColumnState({
      state: [{ colId: sort.sortField, sort: sort.sortOrderAsc ? 'asc' : 'desc' }],
    });
  }, []);

  return (
    <CompanyTable
      onGridReady={handleGridReady}
      onSortChanged={onSortChanged}
      columnDefs={columnSortDefsGet(columnDefs)}
      suppressRowHoverHighlight={false}
      columnPersistenceKey={COLUMN_PERSISTENCE_KEY}
      loadingOverlayComponent={Loader}
      rowData={data}
      noRowsOverlayComponent={NoResults}
      quickFilterText={searchText}
    />
  );
};

export default forwardRef<MonitorTableHandle, MonitorTableProps>(MonitorTable);
